import { Menu } from "@dropbox/dig-components/menu";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_SourceFile } from "@mirage/analytics/events/types/click_source_file";
import { Link } from "@mirage/link/Link";
import { composeSourceToMirage } from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import { ChatSource } from "@mirage/mosaics/Chat/types";
import { stripStackItemFileExtension } from "@mirage/service-stacks/service/utils";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import { FileContentIcon } from "@mirage/shared/icons/FileContentIcon";
import { memo, useMemo } from "react";
import styles from "./ContextPicker.module.css";

export interface ContextPickerSourceRowProps {
  source: ChatSource;
  logComposeEvent?: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
}

export const ContextPickerSourceRow = memo(
  ({
    source,
    logComposeEvent,
    conversationId,
    searchSessionId,
    assistRequestId,
  }: ContextPickerSourceRowProps) => {
    const mirageResult = useMemo(() => composeSourceToMirage(source), [source]);
    const { searchQueryUuid } = useSearchQueryId();

    const isLocalFileSource = source.type === "local_file_content";
    const sourceTitle = useMemo(() => {
      // Strips the .stackitem extension from the source title for sources in Stack Chats.
      // Stack Item sources always come back as dash_search_results within the context of a
      // conversation, so its easier to do this here, rather than in composeSourceToMirage.
      if (mirageResult) {
        return stripStackItemFileExtension(mirageResult.title);
      }
      return "";
    }, [mirageResult?.title]);

    const fileIcon = useMemo(() => {
      return (
        <FileContentIcon
          content={mirageResult!}
          hasBackground={false}
          size="small"
        />
      );
    }, [mirageResult?.uuid]);

    if (!mirageResult) {
      return null;
    }

    return (
      <Menu.Row
        withRightAccessory={fileIcon}
        interactive
        className={isLocalFileSource ? styles.localFileNoPointer : undefined}
      >
        {isLocalFileSource ? (
          <Text className={styles.SourceItemName}>
            <Truncate>{sourceTitle}</Truncate>
          </Text>
        ) : (
          <Link
            variant="monochromatic"
            href={mirageResult.url || ""}
            target="_blank"
            rel="noreferrer"
            hasNoUnderline
            onClick={() => {
              logComposeEvent?.(
                PAP_Click_SourceFile({
                  actionType: "user_input",
                  conversationId,
                  searchSessionId,
                  searchQueryUuid,
                  assistRequestId,
                  resultUuid: mirageResult.uuid,
                }),
              );
            }}
          >
            <Text className={styles.SourceItemName}>
              <Truncate>{sourceTitle}</Truncate>
            </Text>
          </Link>
        )}
      </Menu.Row>
    );
  },
);
ContextPickerSourceRow.displayName = "ContextPickerSourceRow";
